import { useEffect, useState } from 'react'
import { ApiError, api } from '../api/client'
import type { FunnelBreakdown, FunnelDefinition, FunnelStep } from '../api/types'
import { Banner } from './Banner'
import { ConfigList, ConfigRow, type RowChip } from './ConfigList'
import { Drawer } from './Drawer'

interface Props {
  propertyId?: string
}

function blankStep(): FunnelStep {
  return { event_name: '', label: '' }
}

/** The path itself, in one line — which is the whole of what a funnel is. */
function summariseFunnel(funnel: FunnelDefinition): string {
  if (funnel.steps.length === 0) return 'No steps yet'
  return funnel.steps.map((step) => step.label || step.event_name).join(' → ')
}

/**
 * Editor for the ordered funnels reported in every briefing.
 *
 * Step order is the funnel, so steps are moved rather than sorted: reordering the list
 * is the only way to say that checkout comes after the cart.
 *
 * One row per funnel, steps and splits behind a drawer — a funnel's editor is the
 * longest on this page, and stacking several of them made the list unreadable.
 */
export function Funnels({ propertyId }: Props) {
  const [funnels, setFunnels] = useState<FunnelDefinition[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [editing, setEditing] = useState<number | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    api
      .funnels({ propertyId }, controller.signal)
      .then(setFunnels)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setError(cause instanceof Error ? cause.message : 'Could not load funnels.')
      })
    return () => controller.abort()
  }, [propertyId])

  function update(index: number, patch: Partial<FunnelDefinition>) {
    setFunnels((current) =>
      current ? current.map((item, i) => (i === index ? { ...item, ...patch } : item)) : current,
    )
    setDirty(true)
    setNotice(null)
  }

  async function save() {
    if (!funnels) return
    setSaving(true)
    setError(null)
    setNotice(null)

    const prepared = funnels.map((funnel) => ({
      ...funnel,
      steps: funnel.steps
        .map((step) => ({
          event_name: step.event_name.trim(),
          label: step.label.trim() || step.event_name.trim(),
        }))
        .filter((step) => step.event_name),
    }))

    try {
      setFunnels(await api.saveFunnels(prepared, { propertyId }))
      setDirty(false)
      setEditing(null)
      setNotice('Saved. Cached briefings were dropped, so the next one refetches from GA4.')
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not save funnels.')
    } finally {
      setSaving(false)
    }
  }

  if (error && !funnels) return <Banner tone="critical">{error}</Banner>
  if (!funnels) return <p className="placeholder">Loading funnels…</p>

  const open = editing === null ? undefined : funnels[editing]

  return (
    <>
      <p className="section__note" style={{ marginTop: 0 }}>
        Each step is counted as <strong>sessions containing the event</strong>. Every step of a
        funnel is fetched in one GA4 request, so adding a step costs nothing extra; each breakdown
        dimension costs one more.
      </p>

      {error && <Banner tone="critical">{error}</Banner>}
      {notice && <Banner tone="info">{notice}</Banner>}

      <div className="controls" style={{ marginTop: 16 }}>
        <button type="button" className="button" onClick={save} disabled={saving || !dirty}>
          {saving ? 'Saving…' : 'Save funnels'}
        </button>
        {dirty && <span className="section__note">Unsaved changes</span>}
      </div>

      {funnels.length === 0 ? (
        <div className="card">
          <p className="placeholder">No funnels configured — the briefing reports events only.</p>
        </div>
      ) : (
        <ConfigList>
          {funnels.map((funnel, index) => {
            const splits = funnel.breakdowns.filter((item) => item.enabled).length
            const chips: RowChip[] = []
            if (!funnel.enabled) chips.push({ key: 'off', text: 'Off', tone: 'off' })
            chips.push({
              key: 'steps',
              text: `${funnel.steps.length} step${funnel.steps.length === 1 ? '' : 's'}`,
            })
            if (splits > 0) {
              chips.push({ key: 'splits', text: `${splits} split${splits === 1 ? '' : 's'}` })
            }
            return (
              <ConfigRow
                key={funnel.name}
                name={funnel.label || funnel.name}
                code={funnel.name}
                summary={summariseFunnel(funnel)}
                chips={chips}
                dimmed={!funnel.enabled}
                active={editing === index}
                onOpen={() => setEditing(index)}
              />
            )
          })}
        </ConfigList>
      )}

      <Drawer
        open={open !== undefined}
        title={open ? open.label || open.name : ''}
        subtitle={open?.name}
        onClose={() => setEditing(null)}
        footer={
          <>
            <span className="section__note">Saves every funnel, not just this one.</span>
            <button type="button" className="button" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save funnels'}
            </button>
          </>
        }
      >
        {open !== undefined && editing !== null && (
          <FunnelEditor funnel={open} onChange={(patch) => update(editing, patch)} />
        )}
      </Drawer>
    </>
  )
}

interface EditorProps {
  funnel: FunnelDefinition
  onChange: (patch: Partial<FunnelDefinition>) => void
}

function FunnelEditor({ funnel, onChange }: EditorProps) {
  function patchStep(index: number, patch: Partial<FunnelStep>) {
    onChange({ steps: funnel.steps.map((step, i) => (i === index ? { ...step, ...patch } : step)) })
  }

  function move(index: number, delta: number) {
    const target = index + delta
    if (target < 0 || target >= funnel.steps.length) return
    const reordered = [...funnel.steps]
    ;[reordered[index], reordered[target]] = [reordered[target], reordered[index]]
    onChange({ steps: reordered })
  }

  function patchBreakdown(index: number, patch: Partial<FunnelBreakdown>) {
    onChange({
      breakdowns: funnel.breakdowns.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    })
  }

  return (
    <>
      <label className="field">
        <span className="field__label">Shown as</span>
        <input
          value={funnel.label}
          placeholder="Purchase funnel"
          maxLength={80}
          onChange={(e) => onChange({ label: e.target.value })}
        />
      </label>

      <p className="drawer__section">Steps, in order</p>

      {funnel.steps.map((step, index) => (
        <div className="funnel-step" key={index}>
          <div className="drawer__grid">
            <label className="field">
              <span className="field__label">GA4 event name</span>
              <input
                value={step.event_name}
                placeholder="add_to_cart"
                maxLength={100}
                spellCheck={false}
                onChange={(e) => patchStep(index, { event_name: e.target.value })}
              />
            </label>
            <label className="field">
              <span className="field__label">Shown as</span>
              <input
                value={step.label}
                placeholder="Add to cart"
                maxLength={80}
                onChange={(e) => patchStep(index, { label: e.target.value })}
              />
            </label>
          </div>
          <div className="funnel-step__actions">
            <span className="funnel-step__move">
              <button
                type="button"
                className="button secondary"
                aria-label={`Move ${step.label || step.event_name} earlier`}
                disabled={index === 0}
                onClick={() => move(index, -1)}
              >
                ↑
              </button>
              <button
                type="button"
                className="button secondary"
                aria-label={`Move ${step.label || step.event_name} later`}
                disabled={index === funnel.steps.length - 1}
                onClick={() => move(index, 1)}
              >
                ↓
              </button>
            </span>
            <button
              type="button"
              className="button danger"
              onClick={() => onChange({ steps: funnel.steps.filter((_, i) => i !== index) })}
            >
              Remove
            </button>
          </div>
        </div>
      ))}

      <div className="controls" style={{ margin: 0 }}>
        <button
          type="button"
          className="button secondary"
          onClick={() => onChange({ steps: [...funnel.steps, blankStep()] })}
        >
          Add step
        </button>
      </div>

      <p className="drawer__section">Split by</p>

      <p className="breakdown__note" style={{ margin: 0 }}>
        A dimension collected on only some steps must say so. Applying <code>payment_type</code> to{' '}
        <code>add_to_cart</code> returns “(not set)”, which would show every payment method
        converting from zero.
      </p>

      {funnel.breakdowns.map((breakdown, index) => (
        <div className="funnel-step" key={breakdown.key}>
          <div className="drawer__grid">
            <label className="field">
              <span className="field__label">Shown as</span>
              <input
                value={breakdown.label}
                maxLength={80}
                onChange={(e) => patchBreakdown(index, { label: e.target.value })}
              />
            </label>
            <label className="field">
              <span className="field__label">GA4 dimension</span>
              <input
                value={breakdown.ga4_dimension}
                placeholder="customEvent:payment_type"
                maxLength={100}
                spellCheck={false}
                onChange={(e) => patchBreakdown(index, { ga4_dimension: e.target.value })}
              />
            </label>
          </div>
          <div className="funnel-step__actions">
            <span className="field__note">
              {breakdown.applies_to.length === 0
                ? 'Applies to every step'
                : `Applies to ${breakdown.applies_to.join(', ')}`}
            </span>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={breakdown.enabled}
                onChange={(e) => patchBreakdown(index, { enabled: e.target.checked })}
              />
              Enabled
            </label>
          </div>
        </div>
      ))}

      <label className="checkbox">
        <input
          type="checkbox"
          checked={funnel.enabled}
          onChange={(e) => onChange({ enabled: e.target.checked })}
        />
        Enabled
      </label>
    </>
  )
}
